// What the cat is doing right now: one step plays one clip (or holds a pose, or follows the mouse) and moves the cat.
// A step has start(cat, ctx), update(cat, dt, ctx) → true when done, and a tag for the needs (sleep / rest / walk / ...).
import { floorAt, platformBelow, samePlatform } from './world.js';
import { ARRIVE_FORWARD, CLIMB_SPEED } from './rig.js';
import { FALL_BRACE_FRAMES, FALL_FAR } from './policy.js';

export const GRAVITY = 16; // H per second²
const ARC_BASE = 0.8; // H a jump rises above the higher of its two ends...
const ARC_PER_DISTANCE = 0.15; // ...plus this much per H of horizontal distance

const ease = (t) => t * t * (3 - 2 * t);

/** Frames advance at the clip's rate; calls onFrame(k) for every frame passed (so motion never skips a frame). */
class Base {
  constructor(clip, tag) {
    this.clip = clip;
    this.tag = tag;
    this.t = 0;
    this.k = -1;
  }
  advance(dt, speed, count, onFrame) {
    this.t += dt * speed;
    const times = this.clip.times; // per-frame timing (gait changes), when the whole clip plays once
    if (times && count === this.clip.frames) {
      let target = this.k < 0 ? 0 : this.k;
      while (target + 1 < count && times[target + 1] <= this.t) target++;
      while (this.k < target) onFrame(++this.k);
      return this.t >= times[count];
    }
    const target = Math.min(count - 1, Math.floor(this.t * this.clip.fps));
    while (this.k < target) onFrame(++this.k);
    return this.t * this.clip.fps >= count;
  }
}

// Where a frame's drawn cat reaches, relative to its root (display px): its lowest point, and its front-most point (facing right).
// Near a wall the clips' roots are unreliable (they flip between the body's middle and the paws from frame to frame), so the
// wall steps place the cat by these instead: front paws on the wall line, lowest point on a smooth path.
const bottomOf = (clip, k) => (clip.bottom?.[k] ?? 0) * clip.s;
const frontOf = (clip, k) => (clip.right?.[k] ?? 0) * clip.s;

/** One clip on the ground (or a ground → wall start of a climb): measured travel × scale, height from the surface. */
export class ClipStep extends Base {
  constructor(clip, { tag = 'idle', scale = 1, speed = 1 } = {}) {
    super(clip, tag);
    this.scale = scale;
    this.speed = speed;
  }
  start(cat) {
    const c = this.clip;
    this.climbs = c.contact.includes('wall') && cat.wall;
    if (this.climbs) {
      // Jumping onto the wall: the front paws go from where they are to the wall line, the lowest point from the ground up to
      // where it is when hanging on (the root ends where the measured climb puts it)
      const n = c.frames - 1;
      this.front0 = cat.x + cat.dir * frontOf(c, 0);
      this.low0 = cat.y + bottomOf(c, 0);
      this.low1 = cat.y + (c.rootY[n] - c.rootY[0]) * c.s + bottomOf(c, n);
    }
    cat.show(c, 0);
  }
  update(cat, dt) {
    const c = this.clip;
    const done = this.advance(dt, this.speed, c.frames, (k) => {
      if (this.climbs) {
        const p = ease(k / (c.frames - 1));
        cat.x = this.front0 + (cat.wall.x - this.front0) * p - cat.dir * frontOf(c, k);
        cat.y = this.low0 + (this.low1 - this.low0) * p - bottomOf(c, k);
      } else if (k > 0) cat.x += cat.dir * c.dx[k] * this.scale;
      cat.show(c, k);
    });
    if (done) cat.pose = c.to;
    return done;
  }
}

/** A cycle (walk, run) played `cycles` times; travel per cycle × scale. */
export class CycleStep extends Base {
  constructor(clip, cycles, { tag = 'walk', scale = 1, speed = 1 } = {}) {
    super(clip, tag);
    this.cycles = Math.max(1, cycles);
    this.scale = scale;
    this.speed = speed;
  }
  start(cat) {
    cat.show(this.clip, 0);
  }
  update(cat, dt) {
    const c = this.clip;
    const n = c.frames;
    return this.advance(dt, this.speed, n * this.cycles, (k) => {
      const f = k % n;
      if (k > 0) cat.x += cat.dir * (f === 0 ? c.dx[n] : c.dx[f]) * this.scale;
      cat.show(c, f);
    });
  }
}

/** Keep still in a pose for a while (shows the pose's keyframe: frame 0 of a clip that starts there). */
export class HoldStep {
  constructor(clip, seconds, tag = 'rest') {
    this.clip = clip;
    this.seconds = seconds;
    this.tag = tag;
  }
  start(cat) {
    this.t = 0;
    cat.show(this.clip, 0);
  }
  update(cat, dt) {
    this.t += dt;
    return this.t >= this.seconds;
  }
}

/** Turn around (only where the pose looks the same both ways: sitting). */
export class TurnStep {
  tag = 'rest';
  start(cat) {
    cat.dir = -cat.dir;
  }
  update() {
    return true;
  }
}

/** Climb a wall (cycle in place) up to `toY` (root height), at a fixed speed. */
export class ClimbStep extends Base {
  constructor(clip, toY) {
    super(clip, 'climb');
    this.toY = toY;
  }
  start(cat) {
    cat.show(this.clip, 0);
  }
  update(cat, dt, ctx) {
    const c = this.clip;
    this.t += dt;
    const k = Math.floor(this.t * c.fps) % c.frames;
    cat.show(c, k);
    cat.y = Math.max(this.toY, cat.y - CLIMB_SPEED * ctx.H * dt);
    if (cat.wall) cat.x = cat.wall.x - cat.dir * frontOf(c, k); // front paws on the wall
    return cat.y <= this.toY + 0.5;
  }
}

/** Pull up onto the ledge at the top of the wall: ends standing on `top`, a little past the edge. */
export class ArriveStep extends Base {
  constructor(clip, top) {
    super(clip, 'climb');
    this.top = top;
  }
  start(cat, ctx) {
    const c = this.clip;
    const n = c.frames - 1;
    // Lowest point from where it is up onto the ledge; front paws from the wall line to past the edge (where it stands)
    this.low0 = cat.y + bottomOf(c, 0);
    this.front0 = cat.wall ? cat.wall.x : cat.x + cat.dir * frontOf(c, 0);
    this.front1 = this.front0 + cat.dir * (ARRIVE_FORWARD * ctx.H + frontOf(c, n));
    cat.show(c, 0);
  }
  update(cat, dt, ctx) {
    const c = this.clip;
    // The ledge where it is now (its window may have moved since the climb began)
    this.top = samePlatform(ctx.world, this.top) ?? this.top;
    const done = this.advance(dt, 1, c.frames, (k) => {
      const p = k / (c.frames - 1);
      cat.y = this.low0 + (this.top.y - this.low0) * ease(Math.min(1, p * 1.6)) - bottomOf(c, k);
      cat.x = this.front0 + (this.front1 - this.front0) * ease(Math.max(0, (p - 0.4) / 0.6)) - cat.dir * frontOf(c, k);
      cat.show(c, k);
    });
    if (done) {
      cat.pose = c.to;
      cat.surface = this.top;
      cat.wall = null;
    }
    return done;
  }
}

/**
 * A clip with a flight in it (jump, fall, drop): frames before the air phase play in place, the air phase follows a projectile
 * (aimed at `target`, or launched with `velocity`) until it lands on the first platform it comes down on, the rest plays there.
 * The air frames are stretched to the flight time; a flight much shorter than the clip's skips the middle of the air frames.
 * The physics moves the cat's lowest point (paws, or tail tip), not the root: in a drop the root is the scruff with the body
 * hanging a body length below it, so landing is when the lowest point reaches the platform, and the root follows from it frame by frame.
 */
export class AirStep extends Base {
  constructor(clip, { target = null, velocity = null, fromAir = false, tag = 'jump' } = {}) {
    super(clip, tag);
    this.target = target;
    this.velocity = velocity;
    this.fromAir = fromAir;
  }
  start(cat) {
    const [a0, a1] = this.clip.air ?? [0, 1];
    this.a0 = a0;
    this.a1 = a1;
    this.phase = this.fromAir ? 'air' : a0 > 0 ? 'pre' : 'air';
    if (this.fromAir) {
      this.k = a0 - 1;
      this.t = a0 / this.clip.fps;
    }
    this.flight = null;
    this.low0 = cat.y + bottomOf(cat.clip ?? this.clip, cat.frame ?? 0); // the lowest point now (for letting go of a wall)
    cat.show(this.clip, this.phase === 'air' ? a0 : 0);
  }
  /** How far below the root the cat's lowest point is in frame k (display px). */
  bottom(k) {
    return (this.clip.bottom?.[k] ?? 0) * this.clip.s;
  }
  launch(cat, ctx) {
    const H = ctx.H;
    const g = GRAVITY * H;
    // The lowest point, where the flight starts (never below the floor: let go low, nothing was under the feet, and the flight was fitted
    // up to the floor — high off the top of the screen)
    const y0 = Math.min(cat.y + this.bottom(this.a0), floorAt(ctx.world, cat.x, cat.y).y - 1);
    let vx, vy;
    if (this.target) {
      const dx = this.target.x - cat.x;
      const dy = this.target.y - y0;
      // A real arc: the apex is above the higher of the two ends (by more for a longer jump); up to it, then down onto the target
      const apex = Math.min(y0, this.target.y) - (ARC_BASE + ARC_PER_DISTANCE * (Math.abs(dx) / H)) * H;
      const up = Math.sqrt(2 * g * (y0 - apex)); // speed leaving the ground
      const T = up / g + Math.sqrt((2 * (this.target.y - apex)) / g);
      vx = dx / T;
      vy = -up;
    } else [vx, vy] = this.velocity ?? [0, 0];
    // Find where it comes down: the first platform crossed while descending (a jump aimed at another platform can't come back
    // down on the one it left: crossing that one's height on the way down, just past its edge, is not landing on it)
    const targetKeys = this.target?.plat?.keys ?? null;
    const from = cat.surface?.keys ?? null;
    const leaving = targetKeys && from && !from.some((k) => targetKeys.includes(k)) ? from : null;
    let x = cat.x, y = y0, t = 0;
    const step = 1 / 240;
    let land = null;
    for (; t < 6; t += step) {
      const nx = Math.min(ctx.world.maxX - 1, Math.max(ctx.world.minX + 1, x + vx * step));
      const nvy = vy + g * step;
      const ny = y + vy * step + 0.5 * g * step * step;
      if (nvy > 0) land = ctx.world.platforms.find((p) => p.y >= y && p.y <= ny && nx >= p.x0 - 0.05 * H && nx <= p.x1 + 0.05 * H && !(leaving && p.keys.some((k) => leaving.includes(k)))) ?? null;
      x = nx;
      y = ny;
      vy = nvy;
      if (land) break;
    }
    land ??= platformBelow(ctx.world, x, y0);
    // Replayed exactly through the landing point found above (vertical speed solved back from it)
    const T = Math.max(t, 0.05);
    this.flight = { x0: cat.x, y0, vx: (x - cat.x) / T, vy0: (land.y - y0 - 0.5 * g * T * T) / T, T, land, g };
    // Which air frames to show over the flight
    const m = this.a1 - this.a0;
    const want = Math.ceil(this.flight.T * this.clip.fps * 1.6);
    this.airFrames = [];
    if (want >= m) for (let i = 0; i < m; i++) this.airFrames.push(this.a0 + i);
    else {
      for (let i = 0; i < Math.ceil(want / 2); i++) this.airFrames.push(this.a0 + i);
      for (let i = Math.floor(want / 2); i > 0; i--) this.airFrames.push(this.a1 - i);
    }
    this.airT = 0;
    cat.surface = null;
    cat.wall = null;
  }
  update(cat, dt, ctx) {
    const c = this.clip;
    if (this.phase === 'pre') {
      const done = this.advance(dt, 1, this.a0, (k) => {
        if (cat.wall) {
          // Still hanging on (about to let go): front paws on the wall, lowest point where it was
          cat.x = cat.wall.x - cat.dir * frontOf(c, k);
          cat.y = this.low0 - bottomOf(c, k);
        } else if (k > 0) cat.x += cat.dir * c.dx[k];
        cat.show(c, k);
      });
      if (!done) return false;
      this.phase = 'air';
    }
    if (this.phase === 'air') {
      if (!this.flight) this.launch(cat, ctx);
      const f = this.flight;
      this.airT = Math.min(f.T, this.airT + dt);
      const t = this.airT;
      cat.x = f.x0 + f.vx * t;
      const k = this.airFrames[Math.min(this.airFrames.length - 1, Math.floor((t / f.T) * this.airFrames.length))] ?? this.a0;
      cat.y = f.y0 + f.vy0 * t + 0.5 * f.g * t * t - this.bottom(k); // lowest point on the trajectory, root above it
      cat.show(c, k);
      if (t < f.T) return false;
      cat.y = f.land.y - Math.max(0, this.bottom(cat.frame)); // the frame still showing touches down, not its root
      cat.surface = f.land;
      this.phase = 'post';
      this.k = this.a1 - 1;
      this.t = this.k / c.fps;
    }
    const done = this.advance(dt, 1, c.frames, (k) => {
      if (k > this.a1) cat.x += cat.dir * c.dx[k];
      cat.y = (cat.surface ?? this.flight.land).y - Math.max(0, this.bottom(k)); // never through the platform it is on (it may have moved since landing)
      cat.show(c, k);
    });
    if (done) (cat.pose = c.to), (cat.y = (cat.surface ?? this.flight.land).y);
    return done;
  }
}

/** Held by the scruff: the cat hangs from the pointer and struggles until let go. */
export class HeldStep {
  tag = 'held';
  constructor(clip) {
    this.clip = clip;
    this.released = false;
    this.t = 0;
  }
  start(cat) {
    cat.surface = null;
    cat.wall = null;
    cat.pose = 'hang';
    cat.show(this.clip, 0);
  }
  update(cat, dt, ctx) {
    this.t += dt;
    cat.x = ctx.pointer.x;
    cat.y = ctx.pointer.y;
    cat.show(this.clip, Math.floor(this.t * this.clip.fps) % this.clip.frames);
    return this.released;
  }
}

/** Height for a cat standing on its platform: the root on the platform, raised if this frame reaches below the root (never sinks in). */
export function groundY(cat) {
  const c = cat.clip;
  return cat.surface.y - Math.max(0, (c?.bottom?.[cat.frame] ?? 0) * (c?.s ?? 0));
}

/**
 * Falling with nothing under the cat (its platform vanished, it walked off an edge): straight into the falling pose. A drop under
 * FALL_FAR plays only the landing clip's air frames (fitted to the flight, middle skipped if it is short); a higher one loops the
 * falling clip and plays only the landing clip's last air frames (bracing) at the end, so the paws touch down on its landing frame.
 * Like AirStep, the physics moves the cat's lowest point.
 */
export class FallStep {
  tag = 'jump';
  constructor(loop, land, { velocity = [0, 0], floorOnly = false } = {}) {
    this.loop = loop;
    this.land = land;
    this.velocity = velocity;
    this.floorOnly = floorOnly; // past every window, down to the floor (its first appearance)
  }
  bottom(clip, k) {
    return (clip.bottom?.[k] ?? 0) * clip.s;
  }
  start(cat, ctx) {
    const H = ctx.H;
    const g = GRAVITY * H;
    const [a0, a1] = this.land.air ?? [0, this.land.frames];
    this.a1 = a1;
    // Fall from where the cat's lowest point is now
    const low = Math.min(floorAt(ctx.world, cat.x, cat.y).y - 1, cat.surface ? cat.y : cat.y + this.bottom(cat.clip ?? this.loop, cat.frame ?? 0));
    let [vx, vy] = this.velocity;
    let x = cat.x, y = low, t = 0, land = null;
    const step = 1 / 240;
    for (; t < 6 && !land; t += step) {
      const nx = Math.min(ctx.world.maxX - 1, Math.max(ctx.world.minX + 1, x + vx * step));
      const ny = y + vy * step + 0.5 * g * step * step;
      vy += g * step;
      if (vy > 0) land = ctx.world.platforms.find((p) => (!this.floorOnly || p.keys.some((k) => k.startsWith('floor:'))) && p.y > y && p.y <= ny && nx >= p.x0 - 0.05 * H && nx <= p.x1 + 0.05 * H) ?? null;
      x = nx;
      y = ny;
    }
    land ??= platformBelow(ctx.world, x, low + 1);
    const T = Math.max(t, 0.05);
    this.flight = { x0: cat.x, y0: low, vx: (x - cat.x) / T, vy0: (land.y - low - 0.5 * g * T * T) / T, T, land, g };
    // Timeline: high drop = falling loop, then the bracing frames; low drop = the landing clip's air frames fitted to the flight
    const m = a1 - a0;
    this.airFrames = [];
    if (land.y - low >= FALL_FAR * H) {
      const brace = Math.min(FALL_BRACE_FRAMES, m);
      this.loopTime = Math.max(0, T - brace / this.land.fps);
      for (let i = a1 - brace; i < a1; i++) this.airFrames.push(i);
    } else {
      this.loopTime = 0;
      const want = Math.max(1, Math.ceil(T * this.land.fps * 1.6));
      if (want >= m) for (let i = 0; i < m; i++) this.airFrames.push(a0 + i);
      else {
        for (let i = 0; i < Math.ceil(want / 2); i++) this.airFrames.push(a0 + i);
        for (let i = Math.floor(want / 2); i > 0; i--) this.airFrames.push(a1 - i);
      }
    }
    this.t = 0;
    this.phase = 'air';
    cat.surface = null;
    cat.wall = null;
    cat.pose = 'fall';
    this.place(cat);
  }
  /** Frame and position for the current time in the air. */
  place(cat) {
    const f = this.flight;
    const t = Math.min(this.t, f.T);
    let clip, k;
    if (t < this.loopTime) (clip = this.loop), (k = Math.floor(t * this.loop.fps) % this.loop.frames);
    else {
      const rest = Math.max(1e-6, f.T - this.loopTime);
      clip = this.land;
      k = this.airFrames[Math.min(this.airFrames.length - 1, Math.floor(((t - this.loopTime) / rest) * this.airFrames.length))];
    }
    cat.x = f.x0 + f.vx * t;
    cat.y = f.y0 + f.vy0 * t + 0.5 * f.g * t * t - this.bottom(clip, k);
    cat.show(clip, k);
  }
  update(cat, dt) {
    const f = this.flight;
    if (this.phase === 'air') {
      this.t += dt;
      this.place(cat);
      if (this.t < f.T) return false;
      this.phase = 'post';
      this.k = this.a1 - 1;
      this.pt = this.k / this.land.fps;
      cat.surface = f.land;
    }
    const c = this.land;
    this.pt += dt;
    const target = Math.min(c.frames - 1, Math.floor(this.pt * c.fps));
    while (this.k < target) {
      this.k++;
      if (this.k > this.a1) cat.x += cat.dir * c.dx[this.k];
    }
    cat.show(c, this.k);
    cat.y = (cat.surface ?? f.land).y - Math.max(0, this.bottom(c, this.k));
    if (this.pt * c.fps < c.frames) return false;
    cat.pose = c.to;
    cat.y = (cat.surface ?? f.land).y;
    return true;
  }
}

/**
 * A leap to a point however far away — into the app's window, where the cat carries on (the window draws it from then on): crouch,
 * fly a high arc to the point (shrinking to `scale`, the size it has in the window), land, and then hold the last frame. onLanded is
 * called once the landing has played. Nothing in the way counts: it is not a jump within the world.
 */
export class LeapStep extends Base {
  constructor(clip, target, { scale = 1, onLanded = () => {} } = {}) {
    super(clip, 'jump');
    this.target = target;
    this.scaleTo = scale;
    this.onLanded = onLanded;
  }
  start(cat, ctx) {
    const [a0, a1] = this.clip.air ?? [0, this.clip.frames];
    this.a0 = a0;
    this.a1 = a1;
    this.from = { x: cat.x, y: cat.y, scale: cat.scale ?? 1 };
    const dx = this.target.x - cat.x;
    const dist = Math.hypot(dx, this.target.y - cat.y);
    if (Math.abs(dx) > 1) cat.dir = Math.sign(dx);
    this.T = Math.min(1.3, Math.max(0.6, 0.5 + dist / 2500));
    // The apex: above the higher end, more for a longer leap
    this.apex = Math.min(cat.y, this.target.y) - (0.8 * ctx.H + Math.min(0.15 * Math.abs(dx), 4 * ctx.H));
    this.phase = 'pre';
    this.t = 0;
    cat.surface = null;
    cat.wall = null;
    cat.show(this.clip, 0);
  }
  update(cat, dt) {
    const c = this.clip;
    this.t += dt;
    if (this.phase === 'pre') {
      const k = Math.floor(this.t * c.fps);
      if (k < this.a0) return cat.show(c, k), false;
      this.phase = 'air';
      this.t = 0;
    }
    if (this.phase === 'air') {
      const u = Math.min(1, this.t / this.T);
      // Through the apex: a parabola in u from the start height to the target height
      const { x: x0, y: y0, scale: s0 } = this.from;
      const { x: x1, y: y1 } = this.target;
      const h0 = y0 - this.apex;
      const h1 = y1 - this.apex;
      const up = Math.sqrt(h0) / (Math.sqrt(h0) + Math.sqrt(h1)); // share of the time spent rising
      const y = u < up ? this.apex + h0 * ((up - u) / up) ** 2 : this.apex + h1 * ((u - up) / (1 - up)) ** 2;
      cat.x = x0 + (x1 - x0) * u;
      cat.y = y;
      cat.scale = s0 + (this.scaleTo - s0) * u;
      cat.show(c, Math.min(this.a1 - 1, this.a0 + Math.floor(u * (this.a1 - this.a0))));
      if (u < 1) return false;
      this.phase = 'post';
      this.t = 0;
    }
    if (this.phase === 'post') {
      const k = this.a1 + Math.floor(this.t * c.fps);
      if (k < c.frames) return cat.show(c, k), false;
      cat.show(c, c.frames - 1);
      cat.pose = c.to;
      this.phase = 'done';
      this.onLanded();
    }
    return false; // holds the last frame: the window carries on from here
  }
}
